import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * PasswordInput — <Input type="password" /> with a click-to-reveal eye
 * icon on the right. Fully controlled; forwards every unknown prop to the
 * underlying <Input>.
 *
 * Test IDs: if the caller passes `data-testid="foo"`, the toggle button
 * gets `data-testid="foo-toggle"` automatically so end-to-end tests can
 * drive it without re-declaring anything.
 */
export function PasswordInput({
  className = "",
  toggleClassName = "",
  "data-testid": testId,
  ...rest
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        {...rest}
        data-testid={testId}
        type={visible ? "text" : "password"}
        // Reserve right padding so long passwords don't slide under the
        // icon. `pr-10` matches the 40px button width below.
        className={`pr-10 ${className}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        // Tab-orderable so keyboard users can reveal without a mouse.
        // Positioned absolute so the input keeps its default height +
        // border. The `text-muted-foreground` matches the placeholder
        // colour so the icon feels part of the same input.
        className={`absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground transition-colors ${toggleClassName}`}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default PasswordInput;
